import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Styled Components
import {
  ActionButton,
  ButtonRow,
  CompanyName,
  Container,
  ContainerLoader,
  ContainerText,
  ModalContainerStyled,
  Text,
  Title,
} from './styles';

// Types
import { ModalProps } from './types';

// Components
import { Loader } from '../../../inputs/Loader';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

/**
 * Confirmación del borrado DURO de una empresa externa del catálogo maestro.
 *
 * Misma estructura que `ForceCloseRouteModal` (el canónico de la familia de
 * confirmaciones): icono de alerta, bloque de texto agrupado —pregunta, qué se
 * borra y por qué no tiene vuelta atrás— y fila de botones centrada.
 *
 * El borrado solo se ofrece cuando la empresa no tiene actuaciones asociadas
 * (la tabla deshabilita el botón y el backend lo rechaza con
 * EXTERNAL_COMPANY_IN_USE), pero sigue siendo irreversible: por eso se
 * confirma.
 */
export const DeleteExternalCompanyModal = ({
  onClickClose,
  companyName,
  onConfirmDelete,
  isLoading: isLoadingProp,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(isLoadingProp || false);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onConfirmDelete();
      onClickClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <ModalContainerStyled
      width={50}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      {isLoading && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      {/*
        role="dialog" + aria-modal: semántica correcta para un diálogo de
        confirmación (y anclaje estable para los E2E, que si no confunden este
        botón "Eliminar" con los de cada fila de la tabla).
      */}
      <Container
        {...props}
        role="dialog"
        aria-modal="true"
        aria-label={t('pages:management.deleteExternalCompanyConfirmation')}
        isLoading={isLoading}
      >
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">
            {t('pages:management.deleteExternalCompanyConfirmation')}
          </Title>
          <CompanyName variant="semiBold">{companyName}</CompanyName>
          <Text size="M" colorText="greyDark">
            {t('pages:management.deleteExternalCompanyWarning')}
          </Text>
        </ContainerText>

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isLoading}
          />
          <ActionButton
            type="button"
            text={t('common:buttons.delete')}
            onClick={handleDelete}
            disabled={isLoading}
          />
        </ButtonRow>
      </Container>
    </ModalContainerStyled>
  );
};
